<template>
  <div v-if="show" class="modal-overlay" @click.self="closeModal">
    <div class="modal-container" @click.stop>
      <div class="modal-header">
        <div class="header-content">
          <h2>Player GPS Performance Detail</h2>
          <button class="close-btn" @click="closeModal">&times;</button>
        </div>

        <!-- Player Selection for Comparison Mode -->
        <div class="player-comparison-controls" v-if="comparisonMode">
          <div class="selected-players">
            <div v-for="(player, index) in selectedPlayers" :key="player.id" class="player-chip">
              <span class="player-name">{{ player.name }}</span>
              <button @click="removePlayer(index)" class="remove-player">
                &times;
              </button>
            </div>
          </div>
          <button v-if="selectedPlayers.length < 4 && availablePlayers.length > 0"
            @click="showPlayerSelector = !showPlayerSelector" class="add-player-btn">
            + Add Player
          </button>

          <!-- Player Selector Dropdown -->
          <div v-if="showPlayerSelector" class="player-selector-dropdown">
            <div v-for="player in availablePlayers" :key="player.id" @click="addPlayerToComparison(player)"
              class="player-option">
              {{ player.name }}
            </div>
          </div>
        </div>
      </div>

      <div class="modal-body">
        <spinner-loader :loadingStuff="loading" />

        <!-- Toggle Comparison Mode -->
        <div class="toolbar">
          <button @click="toggleComparisonMode" class="btn-comparison" :class="{ active: comparisonMode }">
            <i class="icon-compare"></i>
            {{ comparisonMode ? "Exit Comparison" : "Compare Players" }}
          </button>

          <button @click="exportToPDF" class="btn-export">
            <i class="icon-download"></i> Export PDF
          </button>
        </div>

        <!-- Key Metrics Cards -->
        <div class="metrics-overview">
          <h3>Current Session Metrics</h3>
          <div class="metrics-grid">
            <div v-for="player in selectedPlayers" :key="player.id" class="player-metrics-section"
              :style="{ borderColor: getPlayerColor(player.id) }">
              <h4 v-if="comparisonMode" :style="{ color: getPlayerColor(player.id) }">
                {{ player.name }}
              </h4>

              <div class="metric-card distance">
                <div class="metric-icon">📏</div>
                <div class="metric-content">
                  <div class="metric-value">
                    {{ formatDistance(player.totalDistance) }}
                  </div>
                  <div class="metric-label">Total Distance</div>
                  <div class="metric-comparison" :class="getComparisonClass(player, 'totalDistance')">
                    {{ getComparisonText(player, "totalDistance") }}
                  </div>
                </div>
              </div>

              <div class="metric-card sprint">
                <div class="metric-icon">⚡</div>
                <div class="metric-content">
                  <div class="metric-value">{{ player.sprintEvents }}</div>
                  <div class="metric-label">Sprint Events</div>
                  <div class="metric-comparison" :class="getComparisonClass(player, 'sprintEvents')">
                    {{ getComparisonText(player, "sprintEvents") }}
                  </div>
                </div>
              </div>

              <div class="metric-card speed">
                <div class="metric-icon">🚀</div>
                <div class="metric-content">
                  <div class="metric-value">
                    {{ player.maxSpeed.toFixed(1) }} km/h
                  </div>
                  <div class="metric-label">Top Speed</div>
                  <div class="metric-comparison" :class="getComparisonClass(player, 'maxSpeed')">
                    {{ getComparisonText(player, "maxSpeed") }}
                  </div>
                </div>
              </div>

              <div class="metric-card hsr">
                <div class="metric-icon">🏃</div>
                <div class="metric-content">
                  <div class="metric-value">
                    {{ formatDistance(player.highIntensityDistance) }}
                  </div>
                  <div class="metric-label">High Intensity Running</div>
                  <div class="metric-comparison" :class="getComparisonClass(player, 'highIntensityDistance')">
                    {{ getComparisonText(player, "highIntensityDistance") }}
                  </div>
                </div>
              </div>

              <div class="metric-card acceleration">
                <div class="metric-icon">📈</div>
                <div class="metric-content">
                  <div class="metric-value">
                    {{ player.accelerationEvents }}
                  </div>
                  <div class="metric-label">Accelerations</div>
                  <div class="metric-comparison" :class="getComparisonClass(player, 'accelerationEvents')">
                    {{ getComparisonText(player, "accelerationEvents") }}
                  </div>
                </div>
              </div>

              <div class="metric-card deceleration">
                <div class="metric-icon">📉</div>
                <div class="metric-content">
                  <div class="metric-value">
                    {{ player.decelerationEvents }}
                  </div>
                  <div class="metric-label">Decelerations</div>
                  <div class="metric-comparison" :class="getComparisonClass(player, 'decelerationEvents')">
                    {{ getComparisonText(player, "decelerationEvents") }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Historical Trends Charts -->
        <div v-if="hasHistoricalData" class="historical-trends">
          <div class="trends-header">
            <h3>
              Historical Performance Trends
              <span v-if="historicalData.length >= maxSessionsAvailable">
                (All {{ maxSessionsAvailable }} Sessions)
              </span>
              <span v-else>
                (Last {{ historicalData.length }} of
                {{ maxSessionsAvailable }} Sessions)
              </span>
            </h3>
            <div class="trends-controls">
              <button v-if="historicalData.length < maxSessionsAvailable" @click="loadMoreSessions"
                class="btn-load-more" :disabled="loading">
                <i class="icon-plus">+</i> Load More Sessions
              </button>
              <button v-if="sessionLimit > 5" @click="resetSessionLimit" class="btn-reset" :disabled="loading">
                <i class="icon-reset">↺</i> Show Recent Only
              </button>
            </div>
          </div>
          <div class="charts-grid">
            <div class="chart-item">
              <h4>Total Distance</h4>
              <div class="chart-container">
                <canvas ref="distanceChart"></canvas>
              </div>
            </div>

            <div class="chart-item">
              <h4>Top Speed</h4>
              <div class="chart-container">
                <canvas ref="speedChart"></canvas>
              </div>
            </div>

            <div class="chart-item">
              <h4>Sprint Events</h4>
              <div class="chart-container">
                <canvas ref="sprintChart"></canvas>
              </div>
            </div>

            <div class="chart-item">
              <h4>High Intensity Distance</h4>
              <div class="chart-container">
                <canvas ref="hsrChart"></canvas>
              </div>
            </div>

            <!-- Accelerations & Decelerations not available from PlayerData API
                        <div class="chart-item">
                            <h4>Accelerations</h4>
                            <div class="chart-container">
                                <canvas ref="accelChart"></canvas>
                            </div>
                        </div>

                        <div class="chart-item">
                            <h4>Decelerations</h4>
                            <div class="chart-container">
                                <canvas ref="decelChart"></canvas>
                            </div>
                        </div>
                        -->
          </div>
        </div>

        <!-- No Historical Data Message -->
        <div v-else class="no-historical-data">
          <div class="info-card">
            <i class="icon-info">ℹ️</i>
            <h4>No Historical Data Available</h4>
            <p>
              This athlete only has data for the current session. Historical
              trends will appear once more sessions are recorded.
            </p>
          </div>
        </div>

        <!-- Session-by-Session Breakdown Table -->
        <div v-if="hasHistoricalData" class="session-history">
          <h3>Session History</h3>
          <div class="table-wrapper">
            <table class="history-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Type</th>
                  <th>Distance</th>
                  <th>Top Speed</th>
                  <th>Sprints</th>
                  <th>HSR</th>
                  <th>Accel</th>
                  <th>Decel</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="session in historicalData" :key="session.session_id" :class="{
                  'current-session': session.session_id === currentSessionId,
                }">
                  <td>{{ formatDate(session.session_date) }}</td>
                  <td>
                    <span class="session-type-badge" :class="getSessionTypeClass(session.session_type)">
                      {{ session.session_type }}
                    </span>
                  </td>
                  <td>{{ formatDistance(session.total_distance_m) }}</td>
                  <td>
                    {{
                      session.max_speed_kph
                        ? session.max_speed_kph.toFixed(1)
                        : "-"
                    }}
                    km/h
                  </td>
                  <td>{{ session.sprint_events || 0 }}</td>
                  <td>
                    {{ formatDistance(session.high_intensity_distance_m) }}
                  </td>
                  <td>{{ session.acceleration_events || 0 }}</td>
                  <td>{{ session.deceleration_events || 0 }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Speed Zones Breakdown -->
        <div class="speed-zones-detail">
          <h3>Speed Zones Distribution</h3>
          <div class="zones-comparison">
            <div v-for="player in selectedPlayers" :key="player.id" class="player-zones">
              <h4 v-if="comparisonMode" :style="{ color: getPlayerColor(player.id) }">
                {{ player.name }}
              </h4>
              <div class="zone-bars">
                <div v-for="zone in speedZoneConfig" :key="zone.key" class="zone-bar-row">
                  <div class="zone-label">{{ zone.label }}</div>
                  <div class="zone-bar-container">
                    <div class="zone-bar" :style="{
                      width:
                        getZonePercentage(
                          player.speedZones[zone.key],
                          player.totalZoneTime,
                        ) + '%',
                      backgroundColor: zone.color,
                    }">
                      <span class="zone-value">{{
                        formatZoneTime(player.speedZones[zone.key])
                      }}</span>
                    </div>
                  </div>
                  <div class="zone-percentage">
                    {{
                      getZonePercentage(
                        player.speedZones[zone.key],
                        player.totalZoneTime,
                      ).toFixed(1)
                    }}%
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Period Breakdown (if available) -->
        <div v-if="hasPeriodData" class="period-breakdown">
          <h3>Half-by-Half Analysis</h3>
          <div class="periods-comparison">
            <div v-for="player in selectedPlayers" :key="player.id" class="player-periods">
              <h4 v-if="comparisonMode" :style="{ color: getPlayerColor(player.id) }">
                {{ player.name }}
              </h4>
              <div class="period-metrics">
                <div class="period-card">
                  <h5>First Half</h5>
                  <div class="period-stat">
                    <span class="stat-label">Distance:</span>
                    <span class="stat-value">{{
                      formatDistance(player.firstHalfDistance)
                    }}</span>
                  </div>
                </div>
                <div class="period-card">
                  <h5>Second Half</h5>
                  <div class="period-stat">
                    <span class="stat-label">Distance:</span>
                    <span class="stat-value">{{
                      formatDistance(player.secondHalfDistance)
                    }}</span>
                  </div>
                  <div class="period-stat">
                    <span class="stat-label">Drop-off:</span>
                    <span class="stat-value" :class="getDropOffClass(player)">
                      {{ calculateDropOff(player) }}%
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import SpinnerLoader from "../LoadingSpinning";
import { playerDataApi } from "../../utils/playerDataBackend";
import Chart from "chart.js/auto";
import JsPDF from "jspdf";
import "jspdf-autotable";

export default {
  name: "PlayerGpsDetailModal",
  components: {
    SpinnerLoader,
  },
  props: {
    show: {
      type: Boolean,
      required: true,
    },
    athlete: {
      type: Object,
      default: null,
    },
    allAthletes: {
      type: Array,
      default: () => [],
    },
    sessionId: {
      type: String,
      default: "",
    },
    sessionData: {
      type: Object,
      default: () => ({}),
    },
    sessionAverages: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      loading: false,
      comparisonMode: false,
      selectedPlayers: [],
      showPlayerSelector: false,
      historicalData: [],
      playerHistoricalData: {}, // Store historical data per player ID
      sessionLimit: 5, // Number of historical sessions to display
      maxSessionsAvailable: 0, // Total sessions available in database
      charts: {
        distance: null,
        speed: null,
        sprint: null,
        hsr: null,
        // accel: null, // Not available from PlayerData API
        // decel: null, // Not available from PlayerData API
      },
      currentSessionId: this.sessionId,
      selectedMetrics: [
        "totalDistance",
        "maxSpeed",
        "sprintEvents",
        "highIntensityDistance",
      ],
      availableMetrics: [
        { key: "totalDistance", label: "Total Distance", color: "#39caf5" },
        { key: "maxSpeed", label: "Top Speed", color: "#ff6b6b" },
        { key: "sprintEvents", label: "Sprint Events", color: "#4ecdc4" },
        {
          key: "highIntensityDistance",
          label: "High Intensity Distance",
          color: "#f7b731",
        },
        { key: "accelerationEvents", label: "Accelerations", color: "#4caf50" },
        { key: "decelerationEvents", label: "Decelerations", color: "#f44336" },
        { key: "avgSpeed", label: "Average Speed", color: "#9b59b6" },
      ],
      speedZoneConfig: [
        {
          key: "stationary",
          label: "Stationary/Walk",
          color: "#e0e0e0",
          max: 8.47,
        },
        { key: "jogging", label: "Jogging", color: "#90caf9", max: 12.7 },
        {
          key: "lowIntensity",
          label: "Low Int. Run",
          color: "#66bb6a",
          max: 16.94,
        },
        {
          key: "mediumIntensity",
          label: "Med Int. Run",
          color: "#ffa726",
          max: 23.29,
        },
        {
          key: "highIntensity",
          label: "High Int. Run",
          color: "#ef5350",
          max: 30.49,
        },
        { key: "sprint", label: "Sprint", color: "#ab47bc", max: Infinity },
      ],
      playerColors: ["#39caf5", "#ff6b6b", "#4ecdc4", "#f7b731"],
    };
  },
  computed: {
    availablePlayers() {
      const selectedIds = this.selectedPlayers.map((p) => p.id);
      return this.allAthletes.filter((a) => !selectedIds.includes(a.id));
    },
    hasPeriodData() {
      return this.selectedPlayers.some(
        (p) => p.firstHalfDistance > 0 || p.secondHalfDistance > 0,
      );
    },
    hasHistoricalData() {
      return (
        this.historicalData.length > 0 ||
        Object.values(this.playerHistoricalData).some(
          (data) => data && data.length > 0,
        )
      );
    },
  },
  watch: {
    athlete: {
      immediate: true,
      handler(newAthlete) {
        if (newAthlete && this.show) {
          this.initializeModal();
        }
      },
    },
    show(newVal) {
      if (newVal && this.athlete) {
        this.initializeModal();
      } else if (!newVal) {
        this.cleanupChart();
      }
    },
    selectedPlayers: {
      deep: true,
      handler() {
        if (this.selectedPlayers.length > 0) {
          this.loadHistoricalDataForAllPlayers();
        }
      },
    },
  },
  methods: {
    async initializeModal() {
      this.selectedPlayers = [{ ...this.athlete }];
      this.comparisonMode = false;
      this.showPlayerSelector = false;
      await this.loadHistoricalDataForAllPlayers();
    },

    async loadHistoricalDataForAllPlayers() {
      this.loading = true;

      try {
        // Load historical data for each selected player
        const promises = this.selectedPlayers.map(async (player) => {
          const athleteId = player.athleteId || player.id; // Use athleteId if available
          const data = await this.loadHistoricalData(athleteId);
          return { playerId: player.id, athleteId, data };
        });

        const results = await Promise.all(promises);

        // Store historical data per player
        results.forEach((result) => {
          this.playerHistoricalData[result.playerId] = result.data;
        });

        // Store the first player's history for the main display (backward compatibility)
        if (results[0]) {
          this.historicalData = results[0].data;
        }

        // Wait for next tick to ensure canvas is rendered
        await this.$nextTick();
        this.renderChart();
      } catch (error) {
        console.error("Error loading historical data:", error);
      } finally {
        this.loading = false;
      }
    },

    async loadHistoricalData(athleteId) {
      try {
        // Load more sessions than needed to know if there are more available
        const response = await playerDataApi.getAthleteHistoricalData(
          athleteId,
          50,
        );

        console.log(
          "Historical data response for athlete",
          athleteId,
          ":",
          response,
        );

        // Check if the response has the data directly or nested
        let data = [];
        if (response && Array.isArray(response)) {
          console.log("Response is array, length:", response.length);
          data = response;
        } else if (response && response.data && Array.isArray(response.data)) {
          console.log("Response.data is array, length:", response.data.length);
          data = response.data;
        } else {
          console.warn("No valid historical data found for athlete", athleteId);
          return [];
        }

        // Track max available sessions (before slicing)
        if (data.length > this.maxSessionsAvailable) {
          this.maxSessionsAvailable = data.length;
          console.log("Max sessions available:", this.maxSessionsAvailable);
        }

        // Convert string numbers to actual numbers
        const processedData = data.map((session) => ({
          ...session,
          total_distance_m: parseFloat(session.total_distance_m) || 0,
          max_speed_kph: parseFloat(session.max_speed_kph) || 0,
          avg_speed_kph: parseFloat(session.avg_speed_kph) || 0,
          high_intensity_distance_m:
            parseFloat(session.high_intensity_distance_m) || 0,
          total_sprint_distance_m:
            parseFloat(session.total_sprint_distance_m) || 0,
          sprint_events: parseInt(session.sprint_events) || 0,
          acceleration_events: parseInt(session.acceleration_events) || 0,
          deceleration_events: parseInt(session.deceleration_events) || 0,
        }));

        // Return only the requested number of sessions
        const limited = processedData.slice(0, this.sessionLimit);
        console.log(
          `Returning ${limited.length} of ${processedData.length} available sessions (limit: ${this.sessionLimit})`,
        );
        return limited;
      } catch (error) {
        console.error(
          `Error loading historical data for athlete ${athleteId}:`,
          error,
        );
        return [];
      }
    },

    renderChart() {
      console.log(
        "renderChart called, historicalData.length:",
        this.historicalData.length,
      );
      console.log(
        "playerHistoricalData keys:",
        Object.keys(this.playerHistoricalData),
      );

      // Check if any player has historical data
      const hasAnyData = Object.values(this.playerHistoricalData).some(
        (data) => data && data.length > 0,
      );

      if (!hasAnyData) {
        console.warn("No historical data available to render for any player");
        return;
      }

      this.cleanupChart();

      // Wait for next tick to ensure all canvas elements are rendered
      this.$nextTick(() => {
        // Create four separate charts (Accel/Decel not available from PlayerData API)
        this.createIndividualChart(
          "distanceChart",
          "distance",
          "Total Distance",
          "totalDistance",
          "#39caf5",
          "m",
        );
        this.createIndividualChart(
          "speedChart",
          "speed",
          "Top Speed",
          "maxSpeed",
          "#ff6b6b",
          "km/h",
        );
        this.createIndividualChart(
          "sprintChart",
          "sprint",
          "Sprint Events",
          "sprintEvents",
          "#4ecdc4",
          "",
        );
        this.createIndividualChart(
          "hsrChart",
          "hsr",
          "High Intensity Distance",
          "highIntensityDistance",
          "#f7b731",
          "m",
        );
        // this.createIndividualChart('accelChart', 'accel', 'Accelerations', 'accelerationEvents', '#4caf50', '');
        // this.createIndividualChart('decelChart', 'decel', 'Decelerations', 'decelerationEvents', '#f44336', '');
      });
    },

    createIndividualChart(refName, chartKey, title, metricKey, color, unit) {
      const canvasEl = this.$refs[refName];
      if (!canvasEl || !(canvasEl instanceof HTMLCanvasElement)) {
        console.warn(`Canvas ${refName} not available`);
        return;
      }

      const ctx = canvasEl.getContext("2d");

      // Prepare datasets for each selected player
      const datasets = this.selectedPlayers.map((player, playerIndex) => {
        const playerHistory = this.playerHistoricalData[player.id] || [];
        const playerColor = this.getPlayerColor(player.id);

        return {
          label: player.name,
          data: playerHistory.map((session) =>
            this.getMetricValue(session, metricKey),
          ),
          borderColor: playerColor,
          backgroundColor: playerColor + "33",
          borderWidth: 3,
          tension: 0.4,
          pointRadius: 5,
          pointHoverRadius: 7,
          fill: false,
        };
      });

      // Get labels from the first player's data
      const labelsData =
        this.historicalData.length > 0
          ? this.historicalData
          : Object.values(this.playerHistoricalData).find(
            (d) => d && d.length > 0,
          ) || [];

      const plainLabels = JSON.parse(JSON.stringify(labelsData)).map(
        (session) => this.formatDate(session.session_date),
      );
      const plainDatasets = JSON.parse(JSON.stringify(datasets));

      try {
        this.charts[chartKey] = new Chart(ctx, {
          type: "line",
          data: {
            labels: plainLabels,
            datasets: plainDatasets,
          },
          options: {
            responsive: true,
            maintainAspectRatio: true,
            aspectRatio: 2.5,
            animation: false,
            interaction: {
              mode: "index",
              intersect: false,
            },
            plugins: {
              legend: {
                display: this.comparisonMode,
                position: "top",
                labels: {
                  color: "#ffffff",
                  font: {
                    size: 12,
                  },
                  usePointStyle: true,
                  padding: 15,
                },
              },
              tooltip: {
                backgroundColor: "rgba(0, 0, 0, 0.8)",
                titleColor: "#ffffff",
                bodyColor: "#ffffff",
                borderColor: color,
                borderWidth: 1,
                padding: 12,
                displayColors: true,
                callbacks: {
                  label: function (context) {
                    const label = context.dataset.label || "";
                    const value = context.parsed.y;
                    let formattedValue;
                    if (unit === "m") {
                      formattedValue =
                        value >= 1000
                          ? `${(value / 1000).toFixed(2)} km`
                          : `${value.toFixed(0)} m`;
                    } else if (unit === "km/h") {
                      formattedValue = `${value.toFixed(1)} km/h`;
                    } else {
                      formattedValue = value.toFixed(0);
                    }
                    return `${label}: ${formattedValue}`;
                  },
                },
              },
            },
            scales: {
              x: {
                ticks: {
                  color: "#ffffff",
                  maxRotation: 45,
                  minRotation: 45,
                  font: {
                    size: 11,
                  },
                },
                grid: {
                  color: "rgba(255, 255, 255, 0.1)",
                },
              },
              y: {
                ticks: {
                  color: "#ffffff",
                  font: {
                    size: 11,
                  },
                  callback: function (value) {
                    if (unit === "m" && value >= 1000) {
                      return (value / 1000).toFixed(1) + " km";
                    } else if (unit === "m") {
                      return value + " m";
                    } else if (unit === "km/h") {
                      return value + " km/h";
                    }
                    return value;
                  },
                },
                grid: {
                  color: "rgba(255, 255, 255, 0.1)",
                },
              },
            },
          },
        });
        console.log(`${title} chart created successfully`);
      } catch (error) {
        console.error(`Error creating ${title} chart:`, error);
      }
    },

    updateChart() {
      try {
        console.log("Updating charts - destroying and recreating all charts");
        // Destroy all charts and recreate them
        this.cleanupChart();
        this.$nextTick(() => {
          this.renderChart();
        });
      } catch (error) {
        console.error("Error updating charts:", error);
      }
    },

    getMetricValue(session, metricKey) {
      const mapping = {
        totalDistance: session.total_distance_m || 0,
        maxSpeed: session.max_speed_kph || 0,
        sprintEvents: session.sprint_events || 0,
        highIntensityDistance: session.high_intensity_distance_m || 0,
        accelerationEvents: session.acceleration_events || 0,
        decelerationEvents: session.deceleration_events || 0,
        avgSpeed: session.avg_speed_kph || 0,
      };
      return mapping[metricKey] || 0;
    },

    formatMetricValue(label, value) {
      if (label.includes("Distance")) {
        return this.formatDistance(value);
      } else if (label.includes("Speed")) {
        return `${value.toFixed(1)} km/h`;
      }
      return value.toFixed(0);
    },

    toggleComparisonMode() {
      this.comparisonMode = !this.comparisonMode;
      if (!this.comparisonMode) {
        // Reset to single player
        this.selectedPlayers = [this.selectedPlayers[0]];
      }
    },

    async loadMoreSessions() {
      // Increase limit by 5
      this.sessionLimit += 5;
      await this.loadHistoricalDataForAllPlayers();
    },

    async resetSessionLimit() {
      // Reset to showing only 5 most recent sessions
      this.sessionLimit = 5;
      await this.loadHistoricalDataForAllPlayers();
    },

    addPlayerToComparison(player) {
      if (
        this.selectedPlayers.length < 4 &&
        !this.selectedPlayers.find((p) => p.id === player.id)
      ) {
        this.selectedPlayers.push({ ...player });
        this.showPlayerSelector = false;
      }
    },

    removePlayer(index) {
      if (this.selectedPlayers.length > 1) {
        this.selectedPlayers.splice(index, 1);
      }
    },

    getPlayerColor(playerId) {
      const index = this.selectedPlayers.findIndex((p) => p.id === playerId);
      return this.playerColors[index % this.playerColors.length];
    },

    getComparisonText(player, metric) {
      const sessionAvg =
        this.sessionAverages[
        `avg${metric.charAt(0).toUpperCase() + metric.slice(1)}`
        ];
      if (!sessionAvg) return "";

      const playerValue = player[metric];
      const diff = playerValue - sessionAvg;
      const percentDiff = ((diff / sessionAvg) * 100).toFixed(1);

      if (Math.abs(percentDiff) < 1) return "At average";
      return diff > 0 ? `+${percentDiff}% vs avg` : `${percentDiff}% vs avg`;
    },

    getComparisonClass(player, metric) {
      const sessionAvg =
        this.sessionAverages[
        `avg${metric.charAt(0).toUpperCase() + metric.slice(1)}`
        ];
      if (!sessionAvg) return "";

      const playerValue = player[metric];
      const diff = playerValue - sessionAvg;
      const percentDiff = Math.abs((diff / sessionAvg) * 100);

      if (percentDiff < 1) return "neutral";
      return diff > 0 ? "positive" : "negative";
    },

    calculateDropOff(player) {
      if (player.firstHalfDistance === 0) return 0;
      const dropOff =
        ((player.firstHalfDistance - player.secondHalfDistance) /
          player.firstHalfDistance) *
        100;
      return dropOff.toFixed(1);
    },

    getDropOffClass(player) {
      const dropOff = parseFloat(this.calculateDropOff(player));
      if (dropOff < 5) return "excellent";
      if (dropOff < 10) return "good";
      if (dropOff < 15) return "Progressing well";
      return "concern";
    },

    formatDistance(meters) {
      if (!meters) return "0m";
      if (meters >= 1000) {
        return `${(meters / 1000).toFixed(2)}km`;
      }
      return `${Math.round(meters)}m`;
    },

    formatZoneTime(value) {
      if (!value) return "0%";
      return `${value.toFixed(1)}%`;
    },

    getZonePercentage(zoneValue, totalTime) {
      if (totalTime === 0) return 0;
      return zoneValue; // Already a percentage from API
    },

    formatDate(dateStr) {
      if (!dateStr) return "Unknown";
      const date = new Date(dateStr);
      return date.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    },

    getSessionTypeClass(type) {
      if (type === "MatchSession") return "match";
      if (type === "TrainingSession") return "training";
      return "default";
    },

    async exportToPDF() {
      this.loading = true;

      try {
        const pdf = new JsPDF("p", "mm", "a4");
        const pageWidth = pdf.internal.pageSize.getWidth();
        let yPos = 20;

        // Header
        pdf.setFontSize(18);
        pdf.setTextColor(3, 86, 158);
        pdf.text("GPS Performance Report", pageWidth / 2, yPos, {
          align: "center",
        });
        yPos += 10;

        // Session Info
        pdf.setFontSize(11);
        pdf.setTextColor(0, 0, 0);
        pdf.text(
          `Session Date: ${this.formatDate(this.sessionData.startTime)}`,
          15,
          yPos,
        );
        yPos += 6;
        pdf.text(`Session Type: ${this.sessionData.__typename}`, 15, yPos);
        yPos += 10;

        // Players
        this.selectedPlayers.forEach((player, index) => {
          if (yPos > 250) {
            pdf.addPage();
            yPos = 20;
          }

          pdf.setFontSize(14);
          pdf.setTextColor(57, 202, 245);
          pdf.text(`Player: ${player.name}`, 15, yPos);
          yPos += 8;

          // Key Metrics Table
          pdf.autoTable({
            startY: yPos,
            head: [["Metric", "Value", "vs Session Avg"]],
            body: [
              [
                "Total Distance",
                this.formatDistance(player.totalDistance),
                this.getComparisonText(player, "totalDistance"),
              ],
              [
                "Top Speed",
                `${player.maxSpeed.toFixed(1)} km/h`,
                this.getComparisonText(player, "maxSpeed"),
              ],
              [
                "Sprint Events",
                player.sprintEvents.toString(),
                this.getComparisonText(player, "sprintEvents"),
              ],
              [
                "High Intensity Distance",
                this.formatDistance(player.highIntensityDistance),
                this.getComparisonText(player, "highIntensityDistance"),
              ],
              [
                "Accelerations",
                player.accelerationEvents.toString(),
                this.getComparisonText(player, "accelerationEvents"),
              ],
              [
                "Decelerations",
                player.decelerationEvents.toString(),
                this.getComparisonText(player, "decelerationEvents"),
              ],
            ],
            theme: "grid",
            headStyles: { fillColor: [57, 202, 245] },
            margin: { left: 15, right: 15 },
          });

          yPos = pdf.lastAutoTable.finalY + 10;
        });

        // Historical Data Table
        if (this.historicalData.length > 0) {
          if (yPos > 200) {
            pdf.addPage();
            yPos = 20;
          }

          pdf.setFontSize(12);
          pdf.setTextColor(0, 0, 0);
          pdf.text("Session History", 15, yPos);
          yPos += 5;

          const historyRows = this.historicalData
            .slice(0, 10)
            .map((session) => [
              this.formatDate(session.session_date),
              session.session_type === "MatchSession" ? "Match" : "Training",
              this.formatDistance(session.total_distance_m),
              `${session.max_speed_kph ? session.max_speed_kph.toFixed(1) : "-"} km/h`,
              (session.sprint_events || 0).toString(),
            ]);

          pdf.autoTable({
            startY: yPos,
            head: [["Date", "Type", "Distance", "Top Speed", "Sprints"]],
            body: historyRows,
            theme: "striped",
            headStyles: { fillColor: [100, 100, 100] },
            margin: { left: 15, right: 15 },
          });
        }

        // Footer
        const pageCount = pdf.internal.getNumberOfPages();
        for (let i = 1; i <= pageCount; i++) {
          pdf.setPage(i);
          pdf.setFontSize(9);
          pdf.setTextColor(128, 128, 128);
          pdf.text(
            `Generated: ${new Date().toLocaleDateString("en-GB")} | Page ${i} of ${pageCount}`,
            pageWidth / 2,
            pdf.internal.pageSize.getHeight() - 10,
            { align: "center" },
          );
        }

        // Save PDF
        const fileName = `GPS_Report_${this.selectedPlayers[0].name}_${new Date().toISOString().split("T")[0]}.pdf`;
        pdf.save(fileName);

        this.$toast?.success("PDF exported successfully");
      } catch (error) {
        console.error("Error generating PDF:", error);
        this.$toast?.error("Failed to generate PDF");
      } finally {
        this.loading = false;
      }
    },

    cleanupChart() {
      Object.keys(this.charts).forEach((key) => {
        if (this.charts[key]) {
          this.charts[key].destroy();
          this.charts[key] = null;
        }
      });
    },

    closeModal() {
      this.cleanupChart();
      this.$emit("close");
    },
  },
  beforeUnmount() {
    this.cleanupChart();
  },
};
</script>

<style scoped lang="scss">
.modal-fullscreen {
  max-width: 95% !important;
  max-height: 95vh !important;
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.85);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 20px;
  overflow-y: auto;
}

.modal-container {
  background: #282b33;
  border-radius: 16px;
  max-width: 95vw;
  width: 95%;
  max-height: 95vh;
  overflow-y: auto;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
}

.modal-header {
  background: linear-gradient(135deg, #39caf5 0%, #0497c2 100%);
  padding: 20px 30px;
  border-radius: 16px 16px 0 0;
  position: sticky;
  top: 0;
  z-index: 100;

  .header-content {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 15px;

    h2 {
      margin: 0;
      color: white;
      font-size: 1.8rem;
      font-weight: 700;
    }

    .close-btn {
      background: rgba(255, 255, 255, 0.2);
      border: none;
      color: white;
      font-size: 32px;
      cursor: pointer;
      width: 40px;
      height: 40px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.3s ease;

      &:hover {
        background: rgba(255, 255, 255, 0.3);
        transform: rotate(90deg);
      }
    }
  }

  .player-comparison-controls {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;

    .selected-players {
      display: flex;
      gap: 8px;
      flex-wrap: wrap;

      .player-chip {
        background: rgba(255, 255, 255, 0.2);
        padding: 6px 12px;
        border-radius: 20px;
        display: flex;
        align-items: center;
        gap: 8px;
        color: white;
        font-size: 0.9rem;

        .player-name {
          font-weight: 600;
        }

        .remove-player {
          background: rgba(255, 255, 255, 0.3);
          border: none;
          color: white;
          cursor: pointer;
          width: 20px;
          height: 20px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 16px;

          &:hover {
            background: rgba(255, 255, 255, 0.5);
          }
        }
      }
    }

    .add-player-btn {
      background: rgba(255, 255, 255, 0.2);
      border: 2px dashed rgba(255, 255, 255, 0.5);
      color: white;
      padding: 6px 16px;
      border-radius: 20px;
      cursor: pointer;
      font-weight: 600;
      transition: all 0.3s ease;

      &:hover {
        background: rgba(255, 255, 255, 0.3);
        border-color: white;
      }
    }

    .player-selector-dropdown {
      position: absolute;
      top: 100%;
      left: 0;
      background: #3a3c43;
      border-radius: 8px;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
      max-height: 200px;
      overflow-y: auto;
      z-index: 101;
      margin-top: 5px;
      min-width: 200px;

      .player-option {
        padding: 12px 16px;
        color: white;
        cursor: pointer;
        transition: background 0.2s ease;

        &:hover {
          background: rgba(57, 202, 245, 0.2);
        }

        &:not(:last-child) {
          border-bottom: 1px solid rgba(255, 255, 255, 0.1);
        }
      }
    }
  }
}

.modal-body {
  padding: 30px;
}

.toolbar {
  display: flex;
  gap: 15px;
  margin-bottom: 25px;
  flex-wrap: wrap;

  button {
    padding: 10px 20px;
    border-radius: 8px;
    border: none;
    cursor: pointer;
    font-weight: 600;
    display: flex;
    align-items: center;
    gap: 8px;
    transition: all 0.3s ease;

    &.btn-comparison {
      background: #4ecdc4;
      color: white;

      &.active {
        background: #45a049;
      }

      &:hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(78, 205, 196, 0.4);
      }
    }

    &.btn-export {
      background: #f7b731;
      color: white;

      &:hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(247, 183, 49, 0.4);
      }
    }
  }
}

.metrics-overview {
  margin-bottom: 30px;

  h3 {
    color: #ffffff;
    font-size: 1.4rem;
    margin-bottom: 20px;
  }

  .metrics-grid {
    display: flex;
    flex-direction: column;
    gap: 25px;
  }

  .player-metrics-section {
    border-left: 4px solid #39caf5;
    padding-left: 20px;

    h4 {
      margin: 0 0 15px 0;
      font-size: 1.2rem;
    }

    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 15px;
  }

  .metric-card {
    background: #3a3b43;
    border-radius: 12px;
    padding: 20px;
    display: flex;
    align-items: center;
    gap: 15px;
    transition: all 0.3s ease;
    border: 2px solid transparent;

    &:hover {
      transform: translateY(-3px);
      box-shadow: 0 6px 20px rgba(0, 0, 0, 0.2);
      border-color: #39caf5;
    }

    .metric-icon {
      font-size: 2.5rem;
      line-height: 1;
    }

    .metric-content {
      flex: 1;

      .metric-value {
        font-size: 1.8rem;
        font-weight: 700;
        color: #39caf5;
        margin-bottom: 5px;
      }

      .metric-label {
        font-size: 0.9rem;
        color: #9e9e9e;
        margin-bottom: 8px;
      }

      .metric-comparison {
        font-size: 0.85rem;
        font-weight: 600;
        padding: 4px 8px;
        border-radius: 6px;
        display: inline-block;

        &.positive {
          background: rgba(76, 175, 80, 0.2);
          color: #4caf50;
        }

        &.negative {
          background: rgba(244, 67, 54, 0.2);
          color: #f44336;
        }

        &.neutral {
          background: rgba(158, 158, 158, 0.2);
          color: #9e9e9e;
        }
      }
    }
  }
}

.metrics-selector {
  background: #3a3b43;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 30px;

  h4 {
    color: #ffffff;
    margin-bottom: 15px;
    font-size: 1.1rem;
  }

  .checkbox-group {
    display: flex;
    flex-wrap: wrap;
    gap: 15px;

    label {
      display: flex;
      align-items: center;
      gap: 8px;
      color: #e0e0e0;
      cursor: pointer;
      font-size: 0.95rem;
      transition: color 0.2s ease;

      &:hover {
        color: #39caf5;
      }

      input[type="checkbox"] {
        width: 18px;
        height: 18px;
        cursor: pointer;
        accent-color: #39caf5;
      }
    }
  }
}

.historical-trends {
  background: #3a3b43;
  border-radius: 12px;
  padding: 25px;
  margin-bottom: 30px;

  .trends-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
    flex-wrap: wrap;
    gap: 15px;

    h3 {
      color: #ffffff;
      font-size: 1.3rem;
      margin: 0;
    }

    .trends-controls {
      display: flex;
      gap: 10px;
    }

    .btn-load-more,
    .btn-reset {
      background: linear-gradient(135deg, #39caf5 0%, #0497c2 100%);
      color: white;
      border: none;
      padding: 8px 16px;
      border-radius: 6px;
      cursor: pointer;
      font-size: 0.9rem;
      font-weight: 600;
      transition: all 0.3s ease;
      display: flex;
      align-items: center;
      gap: 6px;

      &:hover:not(:disabled) {
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(57, 202, 245, 0.3);
      }

      &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }

      .icon-plus,
      .icon-reset {
        font-size: 1.1rem;
      }
    }

    .btn-reset {
      background: linear-gradient(135deg, #6c757d 0%, #495057 100%);

      &:hover:not(:disabled) {
        box-shadow: 0 4px 12px rgba(108, 117, 125, 0.3);
      }
    }
  }

  h3 {
    color: #ffffff;
    font-size: 1.3rem;
    margin-bottom: 20px;
  }

  .charts-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 20px;

    @media (max-width: 1200px) {
      grid-template-columns: 1fr;
    }
  }

  .chart-item {
    background: #2c2e35;
    border-radius: 8px;
    padding: 20px;

    h4 {
      color: #39caf5;
      font-size: 1.1rem;
      margin-bottom: 15px;
      text-align: center;
    }

    .chart-container {
      min-height: 300px;

      canvas {
        max-height: 300px;
      }
    }
  }
}

.session-history {
  background: #3a3b43;
  border-radius: 12px;
  padding: 25px;
  margin-bottom: 30px;

  h3 {
    color: #ffffff;
    font-size: 1.3rem;
    margin-bottom: 20px;
  }

  .table-wrapper {
    overflow-x: auto;
  }

  .history-table {
    width: 100%;
    border-collapse: collapse;
    color: #e0e0e0;

    thead {
      background: #2c2e35;

      th {
        padding: 12px;
        text-align: left;
        font-weight: 600;
        color: #39caf5;
        border-bottom: 2px solid #39caf5;
      }
    }

    tbody {
      tr {
        transition: background 0.2s ease;

        &:hover {
          background: rgba(57, 202, 245, 0.1);
        }

        &.current-session {
          background: rgba(247, 183, 49, 0.2);
          font-weight: 600;
        }

        td {
          padding: 12px;
          border-bottom: 1px solid rgba(255, 255, 255, 0.05);
        }
      }
    }

    .session-type-badge {
      display: inline-block;
      padding: 4px 10px;
      border-radius: 12px;
      font-size: 0.8rem;
      font-weight: 600;

      &.match {
        background: rgba(255, 107, 107, 0.2);
        color: #ff6b6b;
      }

      &.training {
        background: rgba(78, 205, 196, 0.2);
        color: #4ecdc4;
      }
    }
  }
}

.speed-zones-detail {
  background: #3a3b43;
  border-radius: 12px;
  padding: 25px;
  margin-bottom: 30px;

  h3 {
    color: #ffffff;
    font-size: 1.3rem;
    margin-bottom: 20px;
  }

  .zones-comparison {
    display: flex;
    flex-direction: column;
    gap: 25px;
  }

  .player-zones {
    h4 {
      margin-bottom: 15px;
      font-size: 1.1rem;
    }

    .zone-bars {
      display: flex;
      flex-direction: column;
      gap: 12px;

      .zone-bar-row {
        display: grid;
        grid-template-columns: 150px 1fr 80px;
        align-items: center;
        gap: 15px;

        .zone-label {
          color: #e0e0e0;
          font-size: 0.9rem;
          font-weight: 500;
        }

        .zone-bar-container {
          background: #2c2e35;
          border-radius: 8px;
          height: 32px;
          overflow: hidden;

          .zone-bar {
            height: 100%;
            display: flex;
            align-items: center;
            padding: 0 10px;
            transition: width 0.3s ease;

            .zone-value {
              color: white;
              font-size: 0.85rem;
              font-weight: 600;
              text-shadow: 0 1px 2px rgba(0, 0, 0, 0.5);
            }
          }
        }

        .zone-percentage {
          color: #39caf5;
          font-weight: 600;
          text-align: right;
        }
      }
    }
  }
}

.period-breakdown {
  background: #3a3b43;
  border-radius: 12px;
  padding: 25px;

  h3 {
    color: #ffffff;
    font-size: 1.3rem;
    margin-bottom: 20px;
  }

  .periods-comparison {
    display: flex;
    flex-direction: column;
    gap: 25px;
  }

  .player-periods {
    h4 {
      margin-bottom: 15px;
      font-size: 1.1rem;
    }

    .period-metrics {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
      gap: 20px;

      .period-card {
        background: #2c2e35;
        border-radius: 10px;
        padding: 20px;

        h5 {
          color: #39caf5;
          margin-bottom: 15px;
          font-size: 1rem;
        }

        .period-stat {
          display: flex;
          justify-content: space-between;
          margin-bottom: 10px;

          .stat-label {
            color: #9e9e9e;
            font-size: 0.9rem;
          }

          .stat-value {
            color: #e0e0e0;
            font-weight: 600;

            &.excellent {
              color: #4caf50;
            }

            &.good {
              color: #8bc34a;
            }

            &.average {
              color: #ffa726;
            }

            &.concern {
              color: #f44336;
            }
          }
        }
      }
    }
  }
}

.no-historical-data {
  background: #3a3b43;
  border-radius: 12px;
  padding: 40px 25px;
  margin-bottom: 30px;
  text-align: center;

  .info-card {
    max-width: 500px;
    margin: 0 auto;

    .icon-info {
      font-size: 3rem;
      margin-bottom: 15px;
      display: block;
    }

    h4 {
      color: #ffffff;
      font-size: 1.3rem;
      margin-bottom: 10px;
    }

    p {
      color: #9e9e9e;
      font-size: 1rem;
      line-height: 1.6;
    }
  }
}

@media (max-width: 768px) {
  .modal-container {
    max-width: 100%;
    max-height: 100vh;
    border-radius: 0;
  }

  .modal-body {
    padding: 15px;
  }

  .metrics-overview .player-metrics-section {
    grid-template-columns: 1fr;
  }

  .speed-zones-detail .player-zones .zone-bars .zone-bar-row {
    grid-template-columns: 1fr;
    gap: 8px;

    .zone-label,
    .zone-percentage {
      text-align: left;
    }
  }

  .toolbar {
    flex-direction: column;

    button {
      width: 100%;
      justify-content: center;
    }
  }
}
</style>
